import { ReactNode } from "react";

import styles from "./SummaryTile.module.css";

/** Icon tint. `accent` is the default; the other three are semantic. */
export type SummaryTone = "accent" | "ok" | "warn" | "danger";

interface Props {
  label: string;
  value: number;
  /** The line under the number, e.g. `7 config fields · 2 boards`. */
  sub: string;
  icon: ReactNode;
  tone?: SummaryTone;
}

const TONE_CLASS: Record<SummaryTone, string> = {
  accent: "",
  ok: styles.iconOk,
  warn: styles.iconWarn,
  danger: styles.iconDanger,
};

/**
 * One headline tile of the import plan screen — mockup
 * .codelane/artifacts/t-27/transfer-import-diff.html L325-L358.
 *
 * Feature-local on purpose. `StatCard` cannot stand in: it takes a mandatory
 * 7-point `series` and always draws a sparkline, which these tiles have none
 * of. The shared look lives in the CSS, which copies StatCard.module.css's
 * `.card` / `.label` / `.icon` / `.value` rules value for value.
 *
 * A zero count renders dim (`.s-value.dim`, mockup L152) — nothing to do in
 * that column reads as absence, not as a number worth the eye.
 */
export function SummaryTile({ label, value, sub, icon, tone = "accent" }: Props) {
  const iconClass = [styles.icon, TONE_CLASS[tone]].filter(Boolean).join(" ");
  const valueClass = [styles.value, value === 0 ? styles.valueDim : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <span className={iconClass} aria-hidden="true">
          {icon}
        </span>
      </div>
      <div className={valueClass}>{value}</div>
      <div className={styles.sub}>{sub}</div>
    </div>
  );
}

/** The tile row itself — `repeat(4, minmax(0, 1fr))`, 12px gap (mockup L143). */
export function SummaryTileRow({ children }: { children: ReactNode }) {
  return <div className={styles.row}>{children}</div>;
}
